import { useState, useEffect, useCallback } from 'react';
import { apiClient, useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { CheckCircle, XCircle, Clock, Shield, FileText, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Spinner, CAUTION_PALIER } from './ChallengeShared';

const STATUS_CONFIG = {
  non_versee: { label: 'Non versee', color: 'text-[#c0392b]', bg: 'bg-[#c0392b]/15', icon: XCircle },
  caution_validee: { label: 'Caution validee', color: 'text-[#FFB800]', bg: 'bg-[#FFB800]/15', icon: Clock },
  engage: { label: 'Engage — Actif', color: 'text-[#2e7d32]', bg: 'bg-[#2e7d32]/15', icon: CheckCircle },
};

export function CautionsTab({ isAdmin }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showValidate, setShowValidate] = useState(null);
  const [validateForm, setValidateForm] = useState({ montant: CAUTION_PALIER, mode: 'especes', commentaire: '' });
  const [saving, setSaving] = useState(false);
  const [showEngagement, setShowEngagement] = useState(null);
  const [engagementChecked, setEngagementChecked] = useState(false);
  const [myProfile, setMyProfile] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [dRes, pRes] = await Promise.allSettled([
        apiClient.get('/challenge/caution-dashboard'),
        apiClient.get('/challenge/mon-profil'),
      ]);
      if (dRes.status === 'fulfilled') setData(dRes.value.data);
      if (pRes.status === 'fulfilled') setMyProfile(pRes.value.data.participant);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleValidate = async () => {
    if (!showValidate) return;
    setSaving(true);
    try {
      await apiClient.put(`/challenge/participants/${showValidate}/caution-validate`, {
        montant: parseInt(validateForm.montant), mode_paiement: validateForm.mode, commentaire: validateForm.commentaire || null
      });
      toast.success('Caution validee !');
      setShowValidate(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleRevoke = async (pid) => {
    if (!confirm('Revoquer la caution ?')) return;
    try {
      await apiClient.put(`/challenge/participants/${pid}/caution-revoke`);
      toast.success('Caution revoquee');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const handleAcceptEngagement = async (pid) => {
    setSaving(true);
    try {
      await apiClient.post(`/challenge/participants/${pid}/accept-engagement`);
      toast.success('Engagement accepte !');
      setShowEngagement(null);
      setEngagementChecked(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  if (loading) return <Spinner />;

  const participants = data?.participants || [];
  const totaux = data?.totaux || {};

  // For all participants: show engagement if caution validated but not yet accepted
  const showMyEngagement = myProfile && myProfile.caution_validated && !myProfile.engagement_accepted;

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="cautions-tab">
      {/* KPI */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-[#c0392b]/15 rounded-lg p-3">
          <p className="font-mono text-xl font-bold text-[#c0392b]">{totaux.non_versee || 0}</p>
          <p className="text-[10px] text-[#AAAAAA]">Non versee</p>
        </div>
        <div className="bg-[#FFB800]/15 rounded-lg p-3">
          <p className="font-mono text-xl font-bold text-[#FFB800]">{totaux.caution_validee || 0}</p>
          <p className="text-[10px] text-[#AAAAAA]">En attente engag.</p>
        </div>
        <div className="bg-[#2e7d32]/15 rounded-lg p-3">
          <p className="font-mono text-xl font-bold text-[#2e7d32]">{totaux.engage || 0}</p>
          <p className="text-[10px] text-[#AAAAAA]">Engages actifs</p>
        </div>
      </div>

      {/* My engagement banner (non-admin) */}
      {showMyEngagement && (
        <div className="bg-[#FFB800]/15 border border-[#FFB800]/30 rounded-lg p-4">
          <p className="text-sm text-white font-bold mb-2">Ta caution a ete validee !</p>
          <p className="text-xs text-zinc-400 mb-3">Lis et accepte l'acte d'engagement pour activer ta participation.</p>
          <Button onClick={() => { setShowEngagement(myProfile.id); setEngagementChecked(false); }}
            className="w-full bg-[#FFB800] text-black font-bold" data-testid="my-engagement-btn">
            <FileText className="w-4 h-4 mr-2" /> Lire et accepter l'acte d'engagement
          </Button>
        </div>
      )}

      {/* Participant list */}
      <div className="space-y-2">
        {participants.map(p => {
          const cfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.non_versee;
          const Icon = cfg.icon;
          return (
            <div key={p.id} className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-3" data-testid={`caution-row-${p.id}`}>
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full ${cfg.bg} flex items-center justify-center`}>
                  <Icon className={`w-4 h-4 ${cfg.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-medium">{p.nom}</p>
                  <p className={`text-[10px] font-semibold ${cfg.color}`}>{cfg.label}</p>
                </div>
                {p.caution_amount && (
                  <span className="font-mono text-xs text-zinc-400">{(p.caution_amount / 1000).toFixed(0)}k XPF</span>
                )}
                {isAdmin && p.status === 'non_versee' && (
                  <Button size="sm" onClick={() => { setShowValidate(p.id); setValidateForm({ montant: CAUTION_PALIER, mode: 'especes', commentaire: '' }); }}
                    className="bg-[#2e7d32] text-white text-xs px-3" data-testid={`validate-caution-${p.id}`}>
                    Valider
                  </Button>
                )}
                {isAdmin && p.status === 'caution_validee' && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => handleRevoke(p.id)}
                      className="border-[#c0392b]/30 text-[#c0392b] text-[10px] px-2">Revoquer</Button>
                  </div>
                )}
                {isAdmin && p.status === 'engage' && (
                  <a href={`/api/challenge/inscription-pdf/${p.id}`} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1 px-2 py-1 bg-[#FF6B35]/15 text-[#FF6B35] rounded text-[10px] font-semibold">
                    <Download className="w-3 h-3" /> PDF
                  </a>
                )}
              </div>
              {p.caution_date && (
                <div className="mt-2 flex flex-wrap gap-2 text-[10px] text-zinc-500">
                  <span>Valide le {new Date(p.caution_date).toLocaleDateString('fr-FR')}</span>
                  <span>par {p.caution_validated_by}</span>
                  <span className="capitalize">{p.caution_payment_mode}</span>
                  {p.engagement_accepted_at && <span className="text-[#2e7d32]">Engage le {new Date(p.engagement_accepted_at).toLocaleDateString('fr-FR')}</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Validate Caution Dialog */}
      <Dialog open={!!showValidate} onOpenChange={() => setShowValidate(null)}>
        <DialogContent className="bg-[#0F0F0F] border-[#2A2A2E] max-w-sm">
          <DialogHeader><DialogTitle className="text-white">Valider la caution</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-400 text-xs">Montant recu (XPF)</Label>
              <Input type="number" value={validateForm.montant} onChange={e => setValidateForm({...validateForm, montant: e.target.value})}
                className="bg-[#1B1B1B] border-[#2A2A2E] text-white font-mono" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Mode de paiement</Label>
              <select value={validateForm.mode} onChange={e => setValidateForm({...validateForm, mode: e.target.value})}
                className="w-full bg-[#1B1B1B] border border-[#2A2A2E] text-white rounded-md px-3 py-2 text-sm">
                <option value="especes">Especes</option>
                <option value="virement">Virement</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Commentaire (optionnel)</Label>
              <Input value={validateForm.commentaire} onChange={e => setValidateForm({...validateForm, commentaire: e.target.value})}
                placeholder="Ex: Recu en main propre" className="bg-[#1B1B1B] border-[#2A2A2E] text-white" />
            </div>
            <Button onClick={handleValidate} disabled={saving} className="w-full bg-[#2e7d32] text-white font-bold">
              {saving ? 'Validation...' : 'Confirmer la validation'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Engagement Dialog */}
      <Dialog open={!!showEngagement} onOpenChange={() => setShowEngagement(null)}>
        <DialogContent className="bg-[#0F0F0F] border-[#2A2A2E] max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#FF6B35]" /> Acte d'Engagement
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-zinc-300">
            <section>
              <h4 className="text-[#FF6B35] font-bold text-xs uppercase tracking-wider mb-2">Caution & Paliers de Restitution</h4>
              <p>Montant de la caution : <strong className="text-white">{CAUTION_PALIER.toLocaleString()} XPF</strong> x 4 paliers = <strong className="text-white">{(CAUTION_PALIER * 4).toLocaleString()} XPF</strong> total.</p>
              <p className="mt-1 text-xs text-zinc-500">Restitution progressive : P1 (J0), P2 (S4 — 33%), P3 (S8 — 66%), P4 (S12 — 100%). Palier rate = montant perdu definitivement vers la cagnotte des lots.</p>
            </section>

            <section>
              <h4 className="text-[#FF6B35] font-bold text-xs uppercase tracking-wider mb-2">Regles du Challenge</h4>
              <ul className="space-y-1 text-xs text-zinc-400">
                <li>• Duree : 12 semaines avec pesees bimensuelles (J0, S2, S4, S6, S8, S10, S12)</li>
                <li>• Donnees mesurees : poids (kg), masse graisseuse (%), masse musculaire (kg)</li>
                <li>• 3 Prix : Recomposition globale, Perte de gras, Prise de muscle</li>
                <li>• Scoring : Score_MG = variation % gras, Score_MM = variation % muscle</li>
                <li>• Anti-cumul : 1 seul prix par participant</li>
                <li>• Garde-fous : Score {"<"} -5 pts = disqualification du prix concerne</li>
              </ul>
            </section>

            <section>
              <h4 className="text-[#FF6B35] font-bold text-xs uppercase tracking-wider mb-2">Obligations du Participant</h4>
              <ul className="space-y-1 text-xs text-zinc-400">
                <li>• Effectuer les 4 versements de caution aux dates prevues</li>
                <li>• Se peser aux 7 dates officielles (a jeun, meme balance, meme heure)</li>
                <li>• Publier dans le groupe Facebook prive (repas, seances, pesees)</li>
                <li>• Participer aux photos de groupe officielles (J0 et S12)</li>
                <li>• Autoriser l'utilisation des photos et donnees pour la communication La Meute</li>
              </ul>
            </section>

            <section className="bg-[#c0392b]/10 border border-[#c0392b]/20 rounded-lg p-3">
              <h4 className="text-[#c0392b] font-bold text-xs uppercase tracking-wider mb-2">Clause de Disqualification</h4>
              <p className="text-xs text-zinc-400">Tout manquement aux regles ci-dessus peut entrainer la disqualification du participant et la perte totale de la caution. Cette decision releve du <strong className="text-white">pouvoir discretionnaire exclusif de Taiau</strong> (coach La Meute) et est <strong className="text-white">sans appel</strong>.</p>
            </section>

            <div className="border-t border-[#2A2A2E] pt-3">
              <button onClick={() => setEngagementChecked(!engagementChecked)}
                className={`w-full flex items-start gap-3 p-3 rounded-md text-left transition-colors ${
                  engagementChecked ? 'bg-[#2e7d32]/15 border border-[#2e7d32]/30' : 'bg-[#1B1B1B] border border-[#2A2A2E]'
                }`} data-testid="engagement-checkbox">
                <div className={`w-5 h-5 rounded-sm flex-shrink-0 flex items-center justify-center mt-0.5 ${engagementChecked ? 'bg-[#2e7d32] text-white' : 'border-2 border-zinc-500'}`}>
                  {engagementChecked && <CheckCircle className="w-3.5 h-3.5" />}
                </div>
                <span className="text-xs text-white font-medium">J'ai lu et j'accepte l'integralite de l'acte d'engagement, les regles du challenge et la clause de disqualification.</span>
              </button>
            </div>

            <Button onClick={() => handleAcceptEngagement(showEngagement)} disabled={saving || !engagementChecked}
              className="w-full bg-[#FF6B35] text-white font-bold hover:bg-[#FF8C55] disabled:opacity-40" data-testid="accept-engagement-btn">
              {saving ? 'Validation...' : "J'accepte et je m'engage"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
